import React, { useState, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { jsPDF } from "jspdf";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";

const JpgToPdfConverter = () => {
    const [images, setImages] = useState([]);
     useTheme();
    const [pdfName, setPdfName] = useState("converted");
    const [isConverting, setIsConverting] = useState(false);
    const [error, setError] = useState("");
    const [layout, setLayout] = useState("portrait"); // 'portrait' or 'landscape'
    const [margin, setMargin] = useState(10); // margin in mm
    const fileInputRef = useRef(null);

    const handleFileUpload = (e) => {
        setError("");
        const files = Array.from(e.target.files);

        if (files.length === 0) return;

        // Filter only image files
        const imageFiles = files.filter(
            (file) =>
                file.type.match("image.*") &&
                (file.name.endsWith(".jpg") || file.name.endsWith(".jpeg"))
        );

        if (imageFiles.length === 0) {
            setError("Please upload valid JPG/JPEG images.");
            return;
        }

        // Create image objects with preview URLs
        const newImages = imageFiles.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
            name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        }));

        setImages((prev) => [...prev, ...newImages]);

        // Set PDF name based on first file if no name is set yet
        if (!pdfName || pdfName === "converted") {
            setPdfName(newImages[0].name);
        }
    };

    const convertToPdf = async () => {
        if (images.length === 0) {
            setError("Please upload at least one image.");
            return;
        }

        setIsConverting(true);
        setError("");

        try {
            // Create a new PDF document
            const pdf = new jsPDF({
                orientation: layout,
                unit: "mm",
            });

            // Process each image
            for (let i = 0; i < images.length; i++) {
                const img = images[i];

                // Create an image element to get dimensions
                const imgElement = await loadImage(img.preview);

                // Calculate dimensions to fit page with margins
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();

                const availableWidth = pageWidth - margin * 2;
                const availableHeight = pageHeight - margin * 2;

                let imgWidth = imgElement.width;
                let imgHeight = imgElement.height;

                // Scale image to fit available space while maintaining aspect ratio
                const widthRatio = availableWidth / imgWidth;
                const heightRatio = availableHeight / imgHeight;
                const ratio = Math.min(widthRatio, heightRatio);

                imgWidth = imgWidth * ratio;
                imgHeight = imgHeight * ratio;

                // Center the image on the page
                const x = (pageWidth - imgWidth) / 2;
                const y = (pageHeight - imgHeight) / 2;

                // Add image to PDF
                pdf.addImage(img.preview, "JPEG", x, y, imgWidth, imgHeight);

                // Add a new page if this isn't the last image
                if (i < images.length - 1) {
                    pdf.addPage();
                }
            }

            // Save the PDF
            pdf.save(`${pdfName}.pdf`);
        } catch (err) {
            setError("Error converting images to PDF. Please try again.");
            console.error(err);
        } finally {
            setIsConverting(false);
        }
    };

    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    };

    const removeImage = (index) => {
        const newImages = [...images];
        URL.revokeObjectURL(newImages[index].preview); // Clean up memory
        newImages.splice(index, 1);
        setImages(newImages);
    };

    const clearAll = () => {
        images.forEach((img) => URL.revokeObjectURL(img.preview)); // Clean up memory
        setImages([]);
        setError("");
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    return (
        <>
            <HeaderSection toolId="jpgToPdf" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
                <div className="bg-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faFilePdf}
                                className="mr-3"
                            />
                            JPG to PDF Converter
                        </h1>
                        <p className="opacity-90">
                            Convert JPG/JPEG images to PDF files with
                            comprehensive validation checks
                        </p>
                    </div>
                    <div className="space-y-6">
                        <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 m-6 p-6">
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".jpg,.jpeg"
                                onChange={handleFileUpload}
                                className="hidden"
                                multiple
                            />
                            <button
                                onClick={triggerFileInput}
                                className="bg-[#74D19C] hover:bg-[#74D19C] text-white font-medium py-2 px-4 rounded-md transition duration-200"
                            >
                                Upload JPG Images
                            </button>
                            <p className="mt-2 text-sm text-gray-500">
                                Supports .jpg, .jpeg files (multiple select
                                available)
                            </p>
                        </div>

                        {images.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 m-6 p-6">
                                <div>
                                    <label
                                        htmlFor="pdf-name"
                                        className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                    >
                                        PDF File Name
                                    </label>
                                    <input
                                        type="text"
                                        id="pdf-name"
                                        value={pdfName}
                                        onChange={(e) =>
                                            setPdfName(e.target.value)
                                        }
                                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="layout"
                                        className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                    >
                                        Page Layout
                                    </label>
                                    <select
                                        id="layout"
                                        value={layout}
                                        onChange={(e) =>
                                            setLayout(e.target.value)
                                        }
                                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="portrait">
                                            Portrait
                                        </option>
                                        <option value="landscape">
                                            Landscape
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label
                                        htmlFor="margin"
                                        className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                    >
                                        Margin (mm)
                                    </label>
                                    <input
                                        type="number"
                                        id="margin"
                                        min="0"
                                        max="50"
                                        value={margin}
                                        onChange={(e) =>
                                            setMargin(parseInt(e.target.value))
                                        }
                                        className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex items-end space-x-2 m-6 p-6">
                                    <button
                                        onClick={convertToPdf}
                                        disabled={isConverting}
                                        className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                                            isConverting
                                                ? "bg-gray-400"
                                                : "bg-green-500 hover:bg-green-600"
                                        } transition duration-200`}
                                    >
                                        {isConverting ? (
                                            <span className="flex items-center justify-center">
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Converting...
                                            </span>
                                        ) : (
                                            "Convert to PDF"
                                        )}
                                    </button>
                                    <button
                                        onClick={clearAll}
                                        className="py-2 px-3 bg-red-500 hover:bg-red-600 text-white rounded-md transition duration-200"
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="h-5 w-5 text-red-500"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm text-red-700">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {images.length > 0 && (
                            <div className="mt-6">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 mb-4">
                                    Selected Images ({images.length})
                                </h2>

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {images.map((image, index) => (
                                        <div
                                            key={index}
                                            className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 relative"
                                        >
                                            <button
                                                onClick={() =>
                                                    removeImage(index)
                                                }
                                                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition duration-200"
                                                title="Remove image"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-4 w-4"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                            <div className="p-2 flex justify-center bg-gray-100">
                                                <img
                                                    src={image.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    className="max-w-full h-auto border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 object-contain"
                                                    style={{
                                                        maxHeight: "200px",
                                                    }}
                                                />
                                            </div>
                                            <div className="p-2 bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 truncate">
                                                {image.file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BottomSection toolId="jpgToPdf" />
        </>
    );
};

export default JpgToPdfConverter;
